/**
 * Brand and user role type definitions
 */

export enum BrandRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

export interface Brand {
  id: string;
  name: string;
  slug?: string;
  domain?: string;
  logo_url?: string;
  icon_url?: string;
  logo_display_mode?: 'icon_only' | 'name_only' | 'icon_and_name';
  default_logo?: {
    id: number;
    source_id: number;
    logo_key: string;
    display_name: string;
    url: string;
    width?: number;
    height?: number;
    file_size_kb?: number;
    is_default: boolean;
    display_order: number;
    created_at?: string;
    updated_at?: string;
  } | null;
  description?: string;
  verified?: boolean;  // Whether this is a verified/claimed brand vs a placeholder
  active?: boolean;    // Whether brand is active (false = archived)
  created_at?: string;
  updated_at?: string;
}

export interface UserBrand extends Brand {
  role: BrandRole;
}

export interface BrandUser {
  id: string;  // User ID (matches backend BrandUserResponse)
  role: BrandRole;
  email: string;
  username: string;
  full_name?: string;
  profile_image_url?: string;  // Added to match backend response
  created_at: string;
  updated_at?: string;
}

export interface AddUserToBrandRequest {
  email: string;
  role: BrandRole;
}

export interface UpdateUserRoleRequest {
  role: BrandRole;
}

export interface BrandWithUsers extends Brand {
  users: BrandUser[];
  user_count?: number;
}

// Invitation types
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

export interface Invitation {
  id: string;
  email: string;
  publisher_id: string;
  publisher_name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  status: InvitationStatus;
  invited_by: string;
  created_at: string;
  expires_at: string;
  is_expired?: boolean;
}

export interface AcceptInvitationRequest {
  token: string;
}

export interface AcceptInvitationResponse {
  id: string;
  publisher_id: string;
  publisher_name: string;
  role: string;
  status: string;
  accepted_at: string;
}

// Role hierarchy for permission checking
export const ROLE_HIERARCHY: Record<BrandRole, number> = {
  [BrandRole.OWNER]: 4,
  [BrandRole.ADMIN]: 3,
  [BrandRole.EDITOR]: 2,
  [BrandRole.VIEWER]: 1,
};

/**
 * Check if a role has sufficient permissions
 * @param userRole - The user's current role
 * @param requiredRole - The minimum required role
 * @returns True if user has sufficient permissions
 */
export const hasRolePermission = (userRole: BrandRole, requiredRole: BrandRole): boolean => {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
};

/**
 * Get display name for role
 */
export const getRoleDisplayName = (role: BrandRole): string => {
  return role.charAt(0).toUpperCase() + role.slice(1);
};
